import { useTheme } from '@shopify/restyle';
import { OpenSettingsPrompt } from 'app/components/open-settings-prompt';

import { MAX_PICTURES, useImagePickerCamera } from 'app/hooks/use-imagepicker-camera';
import type { ImagePickerAsset, ImagePickerSuccessResult } from 'expo-image-picker';
import type { ReactNode } from 'react';
import type { TFunction } from 'i18next';
import type { FieldError } from 'react-hook-form';
import { Platform, Pressable, ScrollView, View } from 'react-native';
import { useTranslation } from 'react-i18next';
import { IconCamera, IconPlus, IconX } from '@tabler/icons-react-native';
import { ImagePickerIcon } from '../icons/imagePicker';
import { Column, Row } from './box';
import { FormError } from './form-error';
import { PCImage } from './image';
import { Text } from './text';
import type { Theme } from 'app/shopify';
import { ICON_STROKE_WIDTH } from 'app/utils/constants';

const DEFAULT_THUMBNAIL_SIZE = 122;
const DEFAULT_EMPTY_HEIGHT = 144;
const COMPOSER_THUMBNAIL_SIZE = 72;
const COMPOSER_EMPTY_HEIGHT = 320;

interface MultiImagePickerProps {
  disabled?: boolean;
  /** Overrides the guidance displayed before any photo is selected. */
  emptyHint?: string;
  error?: FieldError;
  maxImages?: number;
  /** Show cover guidance when the photos belong to a single listing. */
  showCoverLabels?: boolean;
  onChange: (images: ImagePickerAsset[]) => void;
  onOpenCamera?: () => void;
  title: string;
  images?: ImagePickerAsset[];
  variant?: 'default' | 'composer' | 'compact';
  /** When true, renders inline camera/library buttons instead of the native action sheet (needed when inside a Modal on Android) */
  withInlinePicker?: boolean;
  thumbnailSize?: number;
}

type OpenImagePicker = ReturnType<typeof useImagePickerCamera>['onUseImagePickerCamera'];

interface PickerContentProps {
  cameraAction: 'hidden' | 'visible';
  capacity: 'available' | 'full';
  emptyBorderRadius: number;
  emptyHeight: number;
  emptyHint?: string;
  iconColor: string;
  interaction: 'disabled' | 'enabled' | 'picking';
  maxImages: number;
  showCoverLabels: boolean;
  onOpenCamera: () => void;
  onRemoveImage: (index: number) => void;
  onUseImagePickerCamera: OpenImagePicker;
  pickerLayout: 'inline' | 'single-action';
  presentation: 'composer' | 'default' | 'compact';
  t: TFunction;
  tPicker: TFunction;
  theme: Theme;
  thumbnailSize: number;
  title: string;
  value: ImagePickerAsset[];
}

function getDashedBorder(theme: Theme, presentation: PickerContentProps['presentation']) {
  return {
    borderWidth: 1.5,
    borderStyle: 'dashed' as const,
    borderColor: theme.colors['surface.disabled'],
    backgroundColor:
      presentation !== 'default' ? theme.colors['surface.default'] : theme.colors['surface.tertiary-neutral'],
  };
}

function EmptyPickerState({
  cameraAction,
  emptyBorderRadius,
  emptyHeight,
  emptyHint,
  iconColor,
  interaction,
  maxImages,
  onOpenCamera,
  onUseImagePickerCamera,
  pickerLayout,
  presentation,
  t,
  tPicker,
  theme,
  title,
}: PickerContentProps) {
  const iconSize = 36;
  const isComposer = presentation === 'composer';
  const pickerDisabled = interaction !== 'enabled';
  const dashedBorder = getDashedBorder(theme, presentation);

  if (pickerLayout === 'inline') {
    return (
      <Row
        style={{
          height: emptyHeight,
          borderRadius: emptyBorderRadius,
          overflow: 'hidden',
          ...dashedBorder,
        }}
      >
        {cameraAction === 'visible' ? (
          <>
            <EmptyAction
              disabled={pickerDisabled}
              onPress={onOpenCamera}
              icon={<IconCamera strokeWidth={ICON_STROKE_WIDTH} color={iconColor} size={iconSize} />}
              label={tPicker('camera')}
            />
            <View style={{ width: 1, backgroundColor: theme.colors['surface.disabled'] }} />
          </>
        ) : null}
        <EmptyAction
          disabled={pickerDisabled}
          onPress={() => onUseImagePickerCamera({ library: true })}
          icon={<ImagePickerIcon color={iconColor} height={iconSize} width={iconSize} />}
          label={tPicker('library')}
        />
      </Row>
    );
  }

  if (presentation === 'compact') {
    return (
      <Pressable
        disabled={pickerDisabled}
        onPress={() => onUseImagePickerCamera()}
        accessibilityRole="button"
        accessibilityLabel={title}
        style={{ padding: 16, borderRadius: emptyBorderRadius, ...dashedBorder }}
      >
        <Row alignItems="center" gap="spacing.general.x3">
          <Column
            alignItems="center"
            justifyContent="center"
            width={40}
            height={40}
            borderRadius="radius.xl"
            backgroundColor="surface.brand.subtle"
          >
            <IconCamera color={iconColor} size={22} strokeWidth={ICON_STROKE_WIDTH} />
          </Column>
          <Column flex={1} gap="spacing.general.x1">
            <Text fontWeight="600" fontSize={14}>
              {title}
            </Text>
            {emptyHint ? (
              <Text color="text.secondary" fontSize={12} lineHeight={18}>
                {emptyHint}
              </Text>
            ) : null}
          </Column>
          <IconPlus size={20} color={iconColor} />
        </Row>
      </Pressable>
    );
  }

  return (
    <Pressable
      disabled={pickerDisabled}
      onPress={() => onUseImagePickerCamera()}
      accessible
      accessibilityRole="button"
      accessibilityLabel={title}
      style={{
        minHeight: emptyHeight,
        aspectRatio: isComposer ? 1 : undefined,
        borderRadius: emptyBorderRadius,
        alignItems: 'center',
        justifyContent: 'center',
        paddingHorizontal: theme.spacing['spacing.general.x4'],
        paddingVertical: theme.spacing['spacing.general.x8'],
        ...dashedBorder,
      }}
    >
      <View
        style={{
          width: isComposer ? 76 : 64,
          height: isComposer ? 76 : 64,
          borderRadius: 999,
          alignItems: 'center',
          justifyContent: 'center',
          backgroundColor: theme.colors['surface.brand.subtle'],
          marginBottom: isComposer ? theme.spacing['spacing.general.x4'] : 0,
        }}
      >
        <IconCamera strokeWidth={ICON_STROKE_WIDTH} color={iconColor} size={iconSize} />
      </View>
      <Text
        style={{ color: iconColor }}
        fontSize={isComposer ? 18 : undefined}
        fontWeight="700"
        marginTop={isComposer ? undefined : 'spacing.general.x2'}
        textAlign="center"
        variant="bodyBase"
      >
        {title}
      </Text>
      <Text
        style={{ color: theme.colors['text.tertiary'], maxWidth: isComposer ? 220 : undefined }}
        fontSize={12}
        marginTop="spacing.general.x0,5"
        textAlign="center"
        variant="bodyBase"
      >
        {emptyHint ?? t('multiImagePicker.emptyHint', { max: maxImages })}
      </Text>
    </Pressable>
  );
}

function AddMoreTile({
  cameraAction,
  iconColor,
  interaction,
  onOpenCamera,
  onUseImagePickerCamera,
  pickerLayout,
  presentation,
  t,
  tPicker,
  theme,
  thumbnailSize,
}: PickerContentProps) {
  const iconSize = 36;
  const isComposer = presentation === 'composer';
  const pickerDisabled = interaction !== 'enabled';
  const dashedBorder = getDashedBorder(theme, presentation);

  if (pickerLayout === 'inline') {
    return (
      <Column
        style={{
          width: thumbnailSize,
          height: thumbnailSize,
          borderRadius: isComposer ? theme.borderRadii['radius.xl'] : theme.borderRadii['radius.lg'],
          overflow: 'hidden',
          ...dashedBorder,
        }}
      >
        {cameraAction === 'visible' ? (
          <>
            <Pressable
              disabled={pickerDisabled}
              onPress={onOpenCamera}
              accessible
              accessibilityRole="button"
              accessibilityLabel={t('imagePicker.openCamera', { ns: 'accessibility' })}
              style={{ flex: 1, alignItems: 'center', justifyContent: 'center' }}
            >
              <IconCamera strokeWidth={ICON_STROKE_WIDTH} color={iconColor} size={iconSize} />
            </Pressable>
            <View style={{ height: 1, backgroundColor: theme.colors['surface.disabled'] }} />
          </>
        ) : null}
        <Pressable
          disabled={pickerDisabled}
          onPress={() => onUseImagePickerCamera({ library: true })}
          accessible
          accessibilityRole="button"
          accessibilityLabel={t('imagePicker.openLibrary', { ns: 'accessibility' })}
          style={{ flex: 1, alignItems: 'center', justifyContent: 'center' }}
        >
          <ImagePickerIcon color={iconColor} height={iconSize} width={iconSize} />
        </Pressable>
      </Column>
    );
  }

  return (
    <Pressable
      disabled={pickerDisabled}
      onPress={() => onUseImagePickerCamera()}
      accessible
      accessibilityRole="button"
      accessibilityLabel={t('imagePicker.addImage', { ns: 'accessibility' })}
      accessibilityHint={
        presentation === 'compact' ? undefined : t('imagePicker.addImageHint', { ns: 'accessibility' })
      }
      style={{
        width: thumbnailSize,
        height: thumbnailSize,
        borderRadius: isComposer ? theme.borderRadii['radius.xl'] : theme.borderRadii['radius.lg'],
        alignItems: 'center',
        justifyContent: 'center',
        ...dashedBorder,
      }}
    >
      <IconPlus strokeWidth={ICON_STROKE_WIDTH} color={iconColor} size={iconSize} />
      {isComposer ? null : (
        <Text color="text.tertiary" fontSize={11} fontWeight="600" marginTop="spacing.general.x0,5" variant="bodyBase">
          {tPicker('add')}
        </Text>
      )}
    </Pressable>
  );
}

function DefaultImageThumbnail({
  disabled,
  index,
  item,
  presentation,
  showCoverLabels,
  onRemoveImage,
  t,
  theme,
  thumbnailSize,
}: Pick<PickerContentProps, 'onRemoveImage' | 'presentation' | 'showCoverLabels' | 't' | 'theme' | 'thumbnailSize'> & {
  disabled: boolean;
  index: number;
  item: ImagePickerAsset;
}) {
  const uri = item.uri;
  if (!uri) return null;
  const isCover = showCoverLabels && index === 0 && presentation !== 'compact';

  return (
    <View style={{ width: thumbnailSize, height: thumbnailSize }}>
      <PCImage
        height={thumbnailSize}
        width={thumbnailSize}
        source={{ uri }}
        style={{
          borderRadius: theme.borderRadii['radius.lg'],
          backgroundColor: theme.colors['surface.tertiary-neutral'],
        }}
      />

      {isCover ? (
        <View
          style={{
            width: '100%',
            position: 'absolute',
            bottom: 0,
            left: 0,
            paddingHorizontal: 8,
            paddingVertical: 3,
            borderBottomLeftRadius: 999,
            borderBottomRightRadius: 999,
            backgroundColor: theme.colors['text.primary'],
          }}
        >
          <Text color="surface.default" textAlign="center" fontSize={10} fontWeight="700" variant="bodyBase">
            {t('generalSection.coverPhoto')}
          </Text>
        </View>
      ) : null}

      <Pressable
        disabled={disabled}
        onPress={() => onRemoveImage(index)}
        accessible
        accessibilityRole="button"
        accessibilityLabel={t('imagePicker.removeImage', { ns: 'accessibility' })}
        accessibilityHint={t('imagePicker.removeImageHint', { ns: 'accessibility' })}
        hitSlop={8}
        style={{
          position: 'absolute',
          top: -6,
          right: -6,
          width: 24,
          height: 24,
          borderRadius: 12,
          alignItems: 'center',
          justifyContent: 'center',
          backgroundColor: theme.colors['text.primary'],
          boxShadow: '0px 1px 2px rgba(0, 0, 0, 0.2)',
        }}
      >
        <IconX strokeWidth={ICON_STROKE_WIDTH} color={theme.colors['surface.default']} size={14} />
      </Pressable>
    </View>
  );
}

function DefaultFilledState(props: PickerContentProps) {
  const { capacity, interaction, maxImages, presentation, showCoverLabels, t, theme, value } = props;

  return (
    <Column gap="spacing.general.x2">
      <ScrollView
        horizontal
        showsHorizontalScrollIndicator={false}
        contentContainerStyle={{ paddingVertical: 4, paddingRight: 4 }}
      >
        <Row gap="spacing.general.x2" alignItems="center" paddingTop="spacing.general.x2">
          {capacity === 'available' ? <AddMoreTile {...props} /> : null}
          {value.map((item, index) => (
            <DefaultImageThumbnail
              disabled={interaction !== 'enabled'}
              index={index}
              item={item}
              presentation={presentation}
              showCoverLabels={showCoverLabels}
              key={item.uri}
              onRemoveImage={props.onRemoveImage}
              t={t}
              theme={theme}
              thumbnailSize={props.thumbnailSize}
            />
          ))}
        </Row>
      </ScrollView>
      <Row alignItems="center" gap="spacing.general.x1" flexWrap="wrap">
        <Text color="text.tertiary" fontSize={12} fontWeight="600" variant="bodyBase">
          {t('generalSection.pictureCount', { count: value.length, max: maxImages })}
        </Text>
        {showCoverLabels && presentation !== 'compact' ? (
          <Text color="text.tertiary" fontSize={12} variant="bodyBase">
            ·
          </Text>
        ) : null}
        {showCoverLabels && presentation !== 'compact' ? (
          <Text color="text.tertiary" fontSize={12} variant="bodyBase" style={{ flexShrink: 1 }}>
            {t('multiImagePicker.coverHint')}
          </Text>
        ) : null}
      </Row>
    </Column>
  );
}

function ComposerImageThumbnail({
  disabled,
  index,
  item,
  onRemoveImage,
  t,
  theme,
  thumbnailSize,
}: Pick<PickerContentProps, 'onRemoveImage' | 't' | 'theme' | 'thumbnailSize'> & {
  disabled: boolean;
  index: number;
  item: ImagePickerAsset;
}) {
  const uri = item.uri;
  if (!uri) return null;
  const isCover = index === 0;

  return (
    <View style={{ width: thumbnailSize, height: thumbnailSize }}>
      <PCImage
        contentFit="cover"
        source={{ uri }}
        style={{
          width: thumbnailSize,
          height: thumbnailSize,
          borderRadius: theme.borderRadii['radius.xl'],
          backgroundColor: theme.colors['surface.tertiary-neutral'],
          borderWidth: isCover ? 2 : 0,
          borderColor: isCover ? theme.colors['text.brand'] : 'transparent',
        }}
      />

      <Pressable
        onPress={disabled ? undefined : () => onRemoveImage(index)}
        accessible
        accessibilityRole="button"
        accessibilityLabel={t('multiImagePicker.removeImage')}
        accessibilityHint={t('multiImagePicker.removeImageHint')}
        accessibilityState={{ disabled }}
        disabled={disabled}
        hitSlop={disabled ? undefined : 8}
        style={{
          position: 'absolute',
          top: -6,
          right: -6,
          width: 22,
          height: 22,
          borderRadius: 11,
          alignItems: 'center',
          justifyContent: 'center',
          backgroundColor: theme.colors['text.primary'],
        }}
      >
        <IconX strokeWidth={ICON_STROKE_WIDTH} color={theme.colors['surface.default']} size={12} />
      </Pressable>
    </View>
  );
}

function ComposerFilledState(props: PickerContentProps) {
  const { capacity, interaction, maxImages, onRemoveImage, t, theme, thumbnailSize, value } = props;
  const disabled = interaction !== 'enabled';
  const contentContainerStyle = {
    paddingTop: theme.spacing['spacing.general.x2'],
    paddingRight: theme.spacing['spacing.general.x1'],
  };

  return (
    <Column gap="spacing.general.x2">
      <View
        style={{
          width: '100%',
          aspectRatio: 1,
          borderRadius: theme.borderRadii['radius.xl'],
          overflow: 'hidden',
          backgroundColor: theme.colors['surface.tertiary-neutral'],
        }}
      >
        <PCImage contentFit="cover" source={{ uri: value[0]?.uri }} style={{ width: '100%', height: '100%' }} />

        <View
          style={{
            position: 'absolute',
            left: theme.spacing['spacing.general.x2'],
            right: theme.spacing['spacing.general.x2'],
            bottom: theme.spacing['spacing.general.x2'],
            flexDirection: 'row',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}
        >
          <View
            style={{
              paddingHorizontal: theme.spacing['spacing.general.x2'],
              paddingVertical: theme.spacing['spacing.general.x1'],
              borderRadius: 999,
              backgroundColor: 'rgba(22, 22, 22, 0.72)',
            }}
          >
            <Text color="surface.default" fontSize={11} fontWeight="700" variant="bodyBase">
              {t('generalSection.pictureCount', { count: value.length, max: maxImages })}
            </Text>
          </View>

          <Pressable
            onPress={disabled ? undefined : () => onRemoveImage(0)}
            accessible
            accessibilityRole="button"
            accessibilityLabel={t('multiImagePicker.removeImage')}
            accessibilityHint={t('multiImagePicker.removeImageHint')}
            accessibilityState={{ disabled }}
            disabled={disabled}
            hitSlop={disabled ? undefined : 8}
            style={{
              width: 30,
              height: 30,
              borderRadius: 15,
              alignItems: 'center',
              justifyContent: 'center',
              backgroundColor: 'rgba(22, 22, 22, 0.72)',
            }}
          >
            <IconX strokeWidth={ICON_STROKE_WIDTH} color={theme.colors['surface.default']} size={14} />
          </Pressable>
        </View>
      </View>

      <ScrollView horizontal showsHorizontalScrollIndicator={false} contentContainerStyle={contentContainerStyle}>
        <Row alignItems="center" gap="spacing.general.x2">
          {capacity === 'available' ? <AddMoreTile {...props} /> : null}
          {value.map((item, index) => (
            <ComposerImageThumbnail
              disabled={disabled}
              index={index}
              item={item}
              key={item.uri}
              onRemoveImage={onRemoveImage}
              t={t}
              theme={theme}
              thumbnailSize={thumbnailSize}
            />
          ))}
        </Row>
      </ScrollView>
    </Column>
  );
}

function PickerContent(props: PickerContentProps) {
  if (props.value.length === 0) return <EmptyPickerState {...props} />;
  if (props.presentation === 'composer') return <ComposerFilledState {...props} />;
  return <DefaultFilledState {...props} />;
}

export function MultiImagePicker({
  disabled = false,
  emptyHint,
  error,
  maxImages = MAX_PICTURES,
  showCoverLabels = true,
  onChange,
  onOpenCamera,
  title,
  images = [],
  variant = 'default',
  withInlinePicker = false,
  thumbnailSize: thumbnailSizeProp,
}: MultiImagePickerProps) {
  const theme = useTheme<Theme>();
  const { t } = useTranslation('new');
  const { t: tPicker } = useTranslation('image-picker');
  const isComposer = variant === 'composer';

  const value = images.filter((image) => !image.uri.includes('/default/media/'));
  const remaining = Math.max(0, maxImages - value.length);
  const canAddMore = remaining > 0;
  const thumbnailSize = thumbnailSizeProp ?? (isComposer ? COMPOSER_THUMBNAIL_SIZE : DEFAULT_THUMBNAIL_SIZE);
  const emptyHeight = isComposer ? COMPOSER_EMPTY_HEIGHT : DEFAULT_EMPTY_HEIGHT;
  const emptyBorderRadius = isComposer ? theme.borderRadii['radius.xl'] : theme.borderRadii['radius.lg'];
  const showInlineCamera = Platform.OS !== 'web';

  const handleAddImage = (result: ImagePickerSuccessResult) => {
    const newAssets = result.assets.slice(0, remaining);
    onChange([...value, ...newAssets]);
  };

  const handleRemoveImage = (index: number) => {
    onChange(value.filter((_, imageIndex) => imageIndex !== index));
  };

  const { isPickingImage, onUseImagePickerCamera, settingsPromptState } = useImagePickerCamera(
    handleAddImage,
    undefined,
    {
      selectionLimit: remaining,
    },
  );
  const openCamera = onOpenCamera ?? (() => onUseImagePickerCamera({ camera: true }));

  return (
    <>
      <Column gap="spacing.general.x2">
        <PickerContent
          cameraAction={showInlineCamera ? 'visible' : 'hidden'}
          capacity={canAddMore ? 'available' : 'full'}
          emptyBorderRadius={emptyBorderRadius}
          emptyHeight={emptyHeight}
          emptyHint={emptyHint}
          iconColor={theme.colors['text.primary']}
          interaction={disabled ? 'disabled' : isPickingImage ? 'picking' : 'enabled'}
          maxImages={maxImages}
          showCoverLabels={showCoverLabels}
          onOpenCamera={openCamera}
          onRemoveImage={handleRemoveImage}
          onUseImagePickerCamera={onUseImagePickerCamera}
          pickerLayout={withInlinePicker ? 'inline' : 'single-action'}
          presentation={variant}
          t={t}
          tPicker={tPicker}
          theme={theme}
          thumbnailSize={thumbnailSize}
          title={title}
          value={value}
        />

        {error ? <FormError {...{ error }} /> : null}
      </Column>
      <OpenSettingsPrompt
        isOpen={settingsPromptState.isOpen}
        onClose={settingsPromptState.onClose}
        settingName="camera"
      />
    </>
  );
}

function EmptyAction({
  disabled = false,
  onPress,
  icon,
  label,
}: {
  disabled?: boolean;
  onPress: () => void;
  icon: ReactNode;
  label: string;
}) {
  return (
    <Pressable
      disabled={disabled}
      onPress={onPress}
      accessible
      accessibilityRole="button"
      accessibilityLabel={label}
      style={{ flex: 1, alignItems: 'center', justifyContent: 'center' }}
    >
      {icon}
      <Text color="text.primary" fontWeight="600" marginTop="spacing.general.x2" variant="bodyBase">
        {label}
      </Text>
    </Pressable>
  );
}
